<template>
  <div class="index" role="status">
    <notification-panel
      v-if="displayNotification"
      v-bind="getActiveNotification"
      toast
      @close="handleNotificationPanelClose"
    />
  </div>
</template>

<script>
/**
 * Vue: Index (core)
 * -----------------------------------------------------------------------------
 * Used to set index states and global event listeners.
 * - Also updates component loading states when overlays become active.
 * - Displays notifications, container acts as a status live region, example 4p
 *   in these tests https://codepen.io/scottohara/pen/dyzxwyr
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue';
import { mapActions, mapState } from 'pinia';
import useOverlaysStore from '~stores/overlays';
import useCartStore from '~stores/cart';
import useIndexStore from '~stores/index';
import useComponentsStore from '~stores/components';

import breakpoints from '~/config/breakpoints';
import { debounce, isCdnUrl, throttle } from '~/helpers/general';
import { getComponentHandleElement } from '~/helpers/web-components';

import config from '~/../../canvas.config';

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Index',

  components: {
    NotificationPanel: defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.notification-panel' */
          '~async/utils/notification-panel/notification-panel'
        ),
    }),
  },

  data() {
    return {
      cnvsGlobal: true,
      screen: {
        breakpoint: 'xs',
        mobile: true,
        height: 0,
        width: 0,
      },
      scroll: {
        direction: 'none',
        header: 'open',
        top: true,
        x: 0,
        y: 0,
      },
    };
  },

  computed: {
    /**
     * Map Pinia state.
     */
    ...mapState(useComponentsStore, {
      getComponentExists: 'getExists',
    }),

    ...mapState(useIndexStore, {
      getActiveNotification: 'notification',
      pausedScroll: (store) => store.scroll.paused,
      previousUrl: 'previousUrl',
      settings: 'settings',
    }),

    ...mapState(useOverlaysStore, {
      getActiveOverlay: 'getActiveOverlay',
      activeOverlays: 'active',
    }),

    /**
     * Compute if notification should be displayed.
     * @returns {Boolean}
     */
    displayNotification() {
      const notification = this.getActiveNotification;

      if (!notification.displayUrl) {
        return notification;
      }

      /**
       * If the notification display URL matches the previous URL then user has
       * navigated away before notification could close.
       * - Close notification to avoid showing it again.
       * - Can't use previous URL state as it hasn't been updated yet.
       */
      if (notification.displayUrl === sessionStorage.getItem('previousUrl')) {
        this.handleNotificationPanelClose();
        return false;
      }

      return notification.displayUrl === location.pathname;
    },
  },

  watch: {
    /**
     * Watch active overlay to update component loading state.
     */
    getActiveOverlay() {
      const overlay = this.getActiveOverlay;

      if (!overlay?.component) {
        return;
      }

      const components = Array.isArray(overlay.component)
        ? overlay.component
        : [overlay.component];

      components.forEach((handle) => {
        this.loadComponent(handle);

        /**
         * Mirror the `loading` state onto the DOM so external tooling (e.g.
         * an e2e suite) can detect it - `loadComponent()` only updates Pinia.
         * The `active` state is reflected separately once the component
         * itself mounts, see plugins/component-states.js.
         */
        const element = getComponentHandleElement(handle);

        if (element) {
          element.dataset.componentState = cnvs.states.loading;
        }
      });
    },

    /**
     * Watch active notification to load notification panel.
     */
    getActiveNotification() {
      this.loadNotificationPanel();
    },
  },

  mounted() {
    this.rehydrateSite();
    this.setMobileBreakpoints(config.mobileBreakpoints);
    this.getPreviousUrl();

    this.handleResize();
    this.handleScroll(true);

    this.setEventListeners();
    this.setEventBusListeners();

    this.loadNotificationPanel();
    this.determineCartDrawerOpen();
  },

  methods: {
    /**
     * Map Pinia actions.
     */
    ...mapActions(useComponentsStore, {
      loadComponent: 'load',
      loadStylesheet: 'loadStylesheet',
    }),

    ...mapActions(useIndexStore, {
      setMobileBreakpoints: 'setMobileBreakpoints',
      setNotification: 'setNotification',
      setPreviousUrl: 'setPreviousUrl',
      setScreen: 'setScreen',
      setScroll: 'setScroll',
      setStylesheet: 'setStylesheet',
      rehydrateSite: 'rehydrateSite',
    }),
    ...mapActions(useCartStore, { setCartResponse: 'setResponse' }),
    ...mapActions(useOverlaysStore, {
      closeOverlay: 'close',
      openOverlay: 'open',
    }),

    ...mapActions(useOverlaysStore, {
      closeOverlay: 'close',
      openOverlay: 'open',
    }),

    /**
     * Get previous URL from sessionStorage.
     * - State doesn't update in time if set in the `beforeunload` event.
     */
    getPreviousUrl() {
      const previousUrl = sessionStorage.getItem('previousUrl');

      if (previousUrl) {
        this.setPreviousUrl(previousUrl);
      }
    },

    /**
     * Determines depending on the previous URL if the cart drawer should be
     * opened or not.
     */
    determineCartDrawerOpen() {
      if (this.previousUrl !== this.$variable('routes.cart.url')) {
        return;
      }

      this.openOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      });
    },

    /**
     * Set event listeners.
     */
    setEventListeners() {
      this.setStylesheetListeners();

      /**
       * Add default listeners.
       */
      window.visualViewport.addEventListener(
        'resize',
        debounce(() => this.handleResize(), this.settings.throttle),
        { passive: true },
      );

      window.addEventListener(
        'scroll',
        throttle(() => this.handleScroll(), this.settings.throttle),
        { passive: true },
      );

      /**
       * Set previous URL just before changing page.
       * Use two events to cover all devices, beforeunload is ignored by iPhones.
       */
      window.addEventListener('beforeunload', () => {
        sessionStorage.setItem('previousUrl', location.pathname);
      });

      window.addEventListener('pagehide', () => {
        sessionStorage.setItem('previousUrl', location.pathname);
      });
    },

    /**
     * Set EventBus listeners.
     */
    setEventBusListeners() {
      /**
       * Overlay listeners act as fallbacks for non-Vue components.
       * - Also used by cnvs.Helpers.openOverlay() CLI.
       */
      cnvs.EventBus.listen('overlay:open', (overlay) => {
        this.openOverlay(overlay);

        if (overlay.component || !overlay.stylesheet) {
          return;
        }

        this.loadStylesheet(overlay.stylesheet);
      });

      /**
       * Accepts either no argument, a string to close a specifed namespace, or
       * an object to close a namespace with options.
       */
      cnvs.EventBus.listen('overlay:close', (argument) => {
        this.closeOverlay(argument);
      });

      /**
       * Cart update fallback listener.
       */
      cnvs.EventBus.listen('cart:update', async () => {
        await this.setCartResponse();

        if (!this.getComponentExists('cart-drawer')) {
          return;
        }

        this.openOverlay({
          component: 'cart-drawer',
          ignoreDismissed: true,
          namespace: 'cartDrawer',
        });
      });

      /**
       * Set notification fallback listener.
       * - Used for logout notification.
       */
      cnvs.EventBus.listen('cnvs:set-notification', (notification) => {
        this.setNotification(notification);
      });
    },

    /**
     * Listen for stylesheets loading to trigger events after they've loaded.
     */
    setStylesheetListeners() {
      const stylesheets = [...document.querySelectorAll('[rel="stylesheet"]')];

      stylesheets.forEach((element) => {
        const media = element.getAttribute('media');
        let stylesheet = element.getAttribute('href');

        if (isCdnUrl(stylesheet)) {
          stylesheet = stylesheet.split('/').reverse()[0].split('?')[0];
        }

        if (!stylesheet) {
          return;
        }

        /**
         * If it's already loaded then add to state.
         */
        if (media === 'all') {
          this.setStylesheet(stylesheet);
          return;
        }

        /**
         * Otherwise listen for load.
         */
        element.addEventListener('load', () => {
          element.setAttribute('media', 'all');
          this.setStylesheet(stylesheet);
        });
      });
    },

    /**
     * Handle resize.
     */
    handleResize() {
      this.screen = {
        height: window.visualViewport.height,
        width: window.innerWidth,
      };

      /**
       * Set CSS variable.
       */
      document.documentElement.style.setProperty(
        '--viewport-height',
        `${this.screen.height}px`,
      );

      for (const [breakpoint, value] of Object.entries(breakpoints)) {
        if (!window.matchMedia(`(min-width: ${value})`).matches) {
          break;
        }

        this.screen.breakpoint = breakpoint;
      }

      this.screen.mobile = this.settings.mobileBreakpoints.includes(
        this.screen.breakpoint,
      );

      cnvs.EventBus.emit('cnvs:resize', this.screen);
      this.setScreen(this.screen);
    },

    /**
     * Handle scroll.
     * - Don't update state when overlay opens.
     * @param {Boolean} [start] - If setting on initial load.
     */
    handleScroll(start = false) {
      if (this.activeOverlays.length) {
        return;
      }

      /**
       * Set object on start.
       */
      if (start) {
        this.scroll = {
          direction: 'none',
          header: 'open',
          top: window.scrollY <= this.settings.start,
          x: window.scrollX,
          y: window.scrollY,
        };

        return;
      }

      /**
       * If scroll is paused or if scroll difference is less than threshold
       * don't update scroll.
       */
      const difference = window.scrollY - this.scroll.y;

      if (this.pausedScroll || Math.abs(difference) < this.settings.threshold) {
        return;
      }

      this.scroll = {
        direction: difference > 0 ? 'down' : 'up',
        top: window.scrollY <= this.settings.start,
        x: window.scrollX,
        y: window.scrollY,
      };

      if (this.scroll.top) {
        this.scroll.header = 'open';
      } else {
        this.scroll.header = 'minimised';
      }

      cnvs.EventBus.emit('cnvs:scroll', this.scroll);
      this.setScroll(this.scroll);
    },

    /**
     * Loads notification panel on mounted or when active notification changes.
     */
    loadNotificationPanel() {
      if (
        !this.getActiveNotification ||
        this.getComponentActive('notification-panel')
      ) {
        return;
      }

      this.loadComponent('notification-panel');
    },

    /**
     * Handle notification panel close.
     */
    handleNotificationPanelClose() {
      this.setNotification(false);
    },
  },
};
</script>
